import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Loading, useMedplum } from "@medplum/react";

import { Accordion, Group, Paper, Stack, Title } from "@mantine/core";

import { IconNotes } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import {
  clearNewbornSystemsDiffCache,
  getNewbornSystemsDiffHistory,
  NewbornSystemsAnswer,
  NewbornSystemsDiffResult,
} from "@/newborn-systems/services/newborn-systems-diff";

const booleanToYesOrNo = (value: boolean): string => {
  return value ? "Yes" : "No";
};

const NewbornSystemsDiff = ({
  currentVersionIndex,
  versionId,
}: {
  currentVersionIndex: number;
  versionId?: string;
}) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetching, setIsFetching] = useState(true);
  const [histories, setHistories] = useState<NewbornSystemsDiffResult>({});

  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsFetching(true);
      try {
        // Get the encounter to pass to the service
        const encounter = await medplum.readResource("Encounter", encounterId!);

        // Use the newborn systems service that returns the correct format
        const histories = await getNewbornSystemsDiffHistory(
          medplum,
          encounter,
          versionId,
          currentVersionIndex,
        );

        if (mounted) {
          setHistories(histories);
          setIsFetching(false);
        }
      } catch (error) {
        console.error("Error fetching newborn systems diff:", error);
        if (mounted) {
          setIsFetching(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [currentVersionIndex, encounterId, medplum, versionId]);

  // Clear cache when component unmounts to prevent memory leaks
  useEffect(() => {
    return () => {
      if (encounterId) {
        clearNewbornSystemsDiffCache(encounterId);
      }
    };
  }, [encounterId]);

  if (isFetching) return <Loading />;

  // Check if there's any system data in either current or previous versions
  const hasAnyData = Object.values(histories).some((history) => {
    // History is now zipped data: [[current, previous], [current, previous], ...]
    return history.length > 0;
  });

  if (!hasAnyData) {
    return (
      <Paper p="lg" withBorder>
        <Title mb="lg" order={2}>
          Newborn Review of Systems
        </Title>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No system data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  return (
    <Paper p="lg" withBorder>
      <Title mb="lg" order={2}>
        Newborn Review of Systems
      </Title>
      <Accordion multiple>
        {Object.entries(histories)
          .map(([title, history]) => {
            // History is now zipped data: [[current, previous], [current, previous], ...]
            // We need to flatten it to get all current and previous answers
            const allCurrentAnswers: NewbornSystemsAnswer[] = [];
            const allPreviousAnswers: NewbornSystemsAnswer[] = [];

            history.forEach(([current, previous]) => {
              if (current) allCurrentAnswers.push(current);
              if (previous) allPreviousAnswers.push(previous);
            });

            if (allCurrentAnswers.length === 0 && allPreviousAnswers.length === 0) return null;

            // Get system data from answers
            const getSystemDataFromAnswers = (answers: NewbornSystemsAnswer[]) => {
              const systems: Record<
                string,
                {
                  normal: boolean;
                  notNormal: boolean;
                  normalDetails: string[];
                  notNormalDetails: string[];
                  notes: string;
                }
              > = {};

              for (const answer of answers) {
                if (answer?.type === "newborn-system") {
                  systems[answer.systemName] = {
                    normal: answer.normal,
                    notNormal: answer.notNormal,
                    normalDetails: answer.normalDetails,
                    notNormalDetails: answer.notNormalDetails,
                    notes: answer.notes,
                  };
                }
              }

              return systems;
            };

            const currentSystems = getSystemDataFromAnswers(allCurrentAnswers);
            const previousSystems = getSystemDataFromAnswers(allPreviousAnswers);

            // Get all unique system names from both versions
            const allSystemNames = new Set([
              ...Object.keys(currentSystems),
              ...Object.keys(previousSystems),
            ]);

            return Array.from(allSystemNames)
              .map((systemName) => {
                const currentSystem = currentSystems[systemName];
                const previousSystem = previousSystems[systemName];

                if (!currentSystem && !previousSystem) return null;

                let currentAns, previousAns;
                if (!currentSystem && !previousSystem) {
                  currentAns = previousAns = {
                    normal: false,
                    notNormal: false,
                    normalDetails: [],
                    notNormalDetails: [],
                    notes: "",
                  };
                } else {
                  currentAns = currentSystem || {
                    normal: false,
                    notNormal: false,
                    normalDetails: [],
                    notNormalDetails: [],
                    notes: "",
                  };
                  previousAns = previousSystem || {
                    normal: false,
                    notNormal: false,
                    normalDetails: [],
                    notNormalDetails: [],
                    notes: "",
                  };
                  // If only one exists, show the available data in both columns
                  if (!currentSystem) currentAns = previousAns;
                  if (!previousSystem) previousAns = currentAns;
                }

                return (
                  <Accordion.Item key={`${title}-${systemName}`} value={`${title}-${systemName}`}>
                    <Accordion.Control>
                      <Group>
                        <IconNotes size={24} color="#a0a0a0" style={{ marginRight: 8 }} />
                        <TextWrapper c="charcoal.0" variant="bold">
                          {systemName
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())
                            .trim()}
                        </TextWrapper>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Group grow align="stretch">
                        <Paper p="md" withBorder>
                          <Stack>
                            <Title mb="lg" order={4} c="gray.6">
                              Previous
                            </Title>
                            <Stack gap="md">
                              <Stack gap="0">
                                <TextWrapper fw={600}>Normal</TextWrapper>
                                <WordsDiff
                                  showRemoved
                                  current={booleanToYesOrNo(currentAns.normal)}
                                  previous={booleanToYesOrNo(previousAns.normal)}
                                />
                              </Stack>
                              <Stack gap="0">
                                <TextWrapper fw={600}>Not Normal</TextWrapper>
                                <WordsDiff
                                  showRemoved
                                  current={booleanToYesOrNo(currentAns.notNormal)}
                                  previous={booleanToYesOrNo(previousAns.notNormal)}
                                />
                              </Stack>
                              <Stack gap="0">
                                <TextWrapper fw={600}>Normal Details</TextWrapper>
                                <WordsDiff
                                  showRemoved
                                  current={currentAns.normalDetails?.join(", ")}
                                  previous={previousAns.normalDetails?.join(", ")}
                                />
                              </Stack>
                              <Stack gap="0">
                                <TextWrapper fw={600}>Not Normal Details</TextWrapper>
                                <WordsDiff
                                  showRemoved
                                  current={currentAns.notNormalDetails?.join(", ")}
                                  previous={previousAns.notNormalDetails?.join(", ")}
                                />
                              </Stack>
                              <Stack gap="0">
                                <TextWrapper fw={600}>Notes</TextWrapper>
                                <WordsDiff
                                  showRemoved
                                  current={currentAns.notes}
                                  previous={previousAns.notes}
                                />
                              </Stack>
                            </Stack>
                          </Stack>
                        </Paper>
                        <Paper p="md" withBorder>
                          <Stack>
                            <Title mb="lg" order={4} c="gray.6">
                              Amended
                            </Title>
                            <Stack gap="md">
                              <Stack gap="0">
                                <TextWrapper fw={600}>Normal</TextWrapper>
                                <WordsDiff
                                  current={booleanToYesOrNo(currentAns.normal)}
                                  previous={booleanToYesOrNo(previousAns.normal)}
                                />
                              </Stack>
                              <Stack gap="0">
                                <TextWrapper fw={600}>Not Normal</TextWrapper>
                                <WordsDiff
                                  current={booleanToYesOrNo(currentAns.notNormal)}
                                  previous={booleanToYesOrNo(previousAns.notNormal)}
                                />
                              </Stack>
                              <Stack gap="0">
                                <TextWrapper fw={600}>Normal Details</TextWrapper>
                                <WordsDiff
                                  current={currentAns.normalDetails?.join(", ")}
                                  previous={previousAns.normalDetails?.join(", ")}
                                />
                              </Stack>
                              <Stack gap="0">
                                <TextWrapper fw={600}>Not Normal Details</TextWrapper>
                                <WordsDiff
                                  current={currentAns.notNormalDetails?.join(", ")}
                                  previous={previousAns.notNormalDetails?.join(", ")}
                                />
                              </Stack>
                              <Stack gap="0">
                                <TextWrapper fw={600}>Notes</TextWrapper>
                                <WordsDiff
                                  current={currentAns.notes}
                                  previous={previousAns.notes}
                                />
                              </Stack>
                            </Stack>
                          </Stack>
                        </Paper>
                      </Group>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })
              .filter(Boolean);
          })
          .flat()}
      </Accordion>
    </Paper>
  );
};

export default NewbornSystemsDiff;
